import { core } from '@musinsa/pbo-core'
import { IF_REQ_OPTIONS, TRANSPORT_TYPE_OPTIONS } from '@/services/outbound/outboundEtc/consts'

interface Options {
  /** 화주코드 셀렉트 옵션 — 조회 결과에 실린 화주 + 세션 화주. 부모가 소유한다. */
  shipperOptions: { value: string; label: string }[]
  /** 센터명(WH_NM) 옵션 — GET /api/v1/combos 응답(호출부에서 주입, LOGISPRD-12385). */
  warehouseNameOptions?: { value: string; label: string }[]
}

/**
 * GI38 기타출고 표 컬럼 38개. 순서·제목·폭·정렬은 핸드오프 spec.md 표 그대로다
 * (SKUID 는 LOGISPRD-13203, 브랜드ID·업체코드는 업로드 입력용으로 LOGISPRD-13189 에서 추가).
 *
 * `variant` 가 붙은 20개가 편집 셀이고 나머지는 표시 전용이다(spec.md "테이블 동작 계약").
 * `required` 는 저장 시 BE `@NotBlank` 가 걸린 컬럼에 맞춰 헤더에 별표를 띄운다.
 *
 * 정렬·필터(`sortFilter`)와 엑셀형 셀 선택·복사·붙여넣기(`cellSelection`)는 pbo-core `core.Table` 이
 * 기본 on 으로 내장한다. 핸드오프의 선택 적용 기능(enableColumnSortFilter·enableCellSelection)을
 * 위해 레포 공통 컴포넌트를 고칠 일이 없다.
 */
export const buildEtcOutboundColumns = ({ shipperOptions, warehouseNameOptions = [] }: Options): core.CoreColumnType[] => [
  { id: 'c-serviceType', title: '화주타입', dataIndex: 'service_type', width: 110, fixed: 'left' },
  {
    id: 'c-sprNm',
    title: '화주코드',
    dataIndex: 'SPR_NM',
    width: 140,
    required: true,
    variant: 'select',
    variantProps: { options: shipperOptions, showSearch: true },
  },
  { id: 'c-shipperName', title: '화주명', dataIndex: 'shipper_name', width: 140 },
  { id: 'c-ordNo', title: '주문번호', dataIndex: 'ORD_NO', width: 200, required: true, variant: 'input' },
  { id: 'c-ordOptNo', title: '주문일련번호', dataIndex: 'ORD_OPT_NO', width: 210, required: true, variant: 'input' },
  {
    id: 'c-trsType',
    title: '배송유형',
    dataIndex: 'TRS_TYPE',
    width: 140,
    variant: 'select',
    variantProps: { options: TRANSPORT_TYPE_OPTIONS },
  },
  { id: 'c-orderType', title: '주문유형', dataIndex: 'orderType', width: 110 },
  {
    id: 'c-whNm',
    title: '센터명',
    dataIndex: 'WH_NM',
    width: 110,
    required: true,
    variant: 'select',
    variantProps: { options: warehouseNameOptions, showSearch: true },
  },
  { id: 'c-whArea', title: '층정보', dataIndex: 'WH_AREA', width: 100, required: true, variant: 'input' },
  { id: 'c-plnDate', title: '출고예정일자', dataIndex: 'PLN_DATE', width: 140, variant: 'datepicker' },
  { id: 'c-uid', title: 'UID', dataIndex: 'UID', width: 160 },
  { id: 'c-brdNm', title: '브랜드', dataIndex: 'BRD_NM', width: 140 },
  { id: 'c-brdId', title: '브랜드ID', dataIndex: 'BRD_ID', width: 140 },
  { id: 'c-comCd', title: '업체코드', dataIndex: 'COM_CD', width: 130 },
  { id: 'c-stlNo', title: '스타일넘버', dataIndex: 'STL_NO', width: 140 },
  { id: 'c-barcode', title: '바코드', dataIndex: 'BARCODE', width: 175, required: true, variant: 'input' },
  { id: 'c-skuId', title: 'SKUID', dataIndex: 'SKU_ID', width: 150 },
  { id: 'c-gdsCd', title: '상품코드', dataIndex: 'GDS_CD', width: 130 },
  { id: 'c-optionCode', title: '옵션코드', dataIndex: 'option_code', width: 130 },
  { id: 'c-gdsNm', title: '상품명', dataIndex: 'GDS_NM', width: 240 },
  { id: 'c-ordQty', title: '주문수량', dataIndex: 'ORD_QTY', width: 120, align: 'right', required: true, variant: 'input' },
  { id: 'c-gift', title: '사은품', dataIndex: 'GIFT', width: 140, variant: 'input' },
  { id: 'c-procNm', title: '출고메시지', dataIndex: 'PROC_NM', width: 200, variant: 'input' },
  { id: 'c-trsMsg', title: '특이사항', dataIndex: 'TRS_MSG', width: 160, variant: 'input' },
  { id: 'c-trsNm', title: '수령자', dataIndex: 'TRS_NM', width: 100, required: true, variant: 'input' },
  { id: 'c-ordNm', title: '주문자명', dataIndex: 'ORD_NM', width: 100, required: true, variant: 'input' },
  { id: 'c-trsPhone', title: '전화번호', dataIndex: 'TRS_PHONE', width: 140, required: true, variant: 'input' },
  { id: 'c-trsMobile', title: '핸드폰', dataIndex: 'TRS_MOBILE', width: 150, required: true, variant: 'input' },
  { id: 'c-trsZip', title: '우편번호', dataIndex: 'TRS_ZIP', width: 100, required: true, variant: 'input' },
  { id: 'c-trsAddr', title: '주소', dataIndex: 'TRS_ADDR', width: 210, required: true, variant: 'input' },
  { id: 'c-trsPass', title: '출입방법', dataIndex: 'TRS_PASS', width: 140, variant: 'input' },
  { id: 'c-carrier', title: '운송사', dataIndex: 'CARRIER', width: 120 },
  { id: 'c-gdsOpt', title: '옵션', dataIndex: 'GDS_OPT', width: 100 },
  {
    id: 'c-ifReq',
    title: '인터페이스 구분',
    dataIndex: 'IF_REQ',
    width: 140,
    align: 'center',
    variant: 'select',
    variantProps: { options: IF_REQ_OPTIONS },
  },
  { id: 'c-insDate', title: '생성일자', dataIndex: 'INS_DATE', width: 120 },
  { id: 'c-insBy', title: '생성자', dataIndex: 'INS_BY', width: 100 },
  { id: 'c-updDate', title: '수정일자', dataIndex: 'UPD_DATE', width: 120 },
  { id: 'c-updBy', title: '수정자', dataIndex: 'UPD_BY', width: 100 },
]

/**
 * 저장 전 필수값 검사 대상 — 표 헤더에 별표가 붙는 컬럼과 **같은 출처**에서 나온다.
 *
 * 두 곳에 따로 적으면 언젠가 갈라진다. 사용자는 별표를 보고 필수로 아는데 검사가 없으면
 * 비운 채 저장이 시도되고 BE 400 으로 튕긴다. 파생시켜 그 가능성 자체를 없앤다.
 * (1.0 `CommonUtil.onSaveData` 가 컬럼 정의의 required 를 전수 훑던 자리다.)
 */
export const toRequiredColumns = (columns: core.CoreColumnType[]) =>
  columns.filter((c) => c.required).map((c) => ({ key: String(c.dataIndex), title: String(c.title) }))

export const REQUIRED_COLUMNS = toRequiredColumns(buildEtcOutboundColumns({ shipperOptions: [] }))

/** 엑셀 다운로드·업로드가 쓰는 (헤더 제목 → 행 키) 대응. 표 컬럼 순서를 그대로 따른다. */
export const EXCEL_COLUMNS = buildEtcOutboundColumns({ shipperOptions: [] }).map((c) => ({
  title: String(c.title),
  key: String(c.dataIndex),
}))

/**
 * 표에서는 못 고치고 엑셀 업로드로만 받는 상품정보 (LOGISPRD-13189).
 * BE 는 flag ON 이면 상품정보를 이 입력값만으로 저장한다 (LOGISPRD-13160).
 */
const UPLOAD_ONLY_KEYS = new Set(['UID', 'BRD_NM', 'BRD_ID', 'COM_CD', 'STL_NO', 'option_code', 'GDS_NM', 'GDS_OPT'])

/** 엑셀 업로드로 값을 덮을 수 있는 컬럼 — 편집 셀(variant 가 붙은 20개) + 업로드 전용 8개. 표 컬럼 순서다. */
export const IMPORTABLE_KEYS = buildEtcOutboundColumns({ shipperOptions: [] })
  .filter((c) => c.variant || UPLOAD_ONLY_KEYS.has(String(c.dataIndex)))
  .map((c) => String(c.dataIndex))
